import { countries, globalCompanies } from '../data/globalRegistry.ts'
import type { IntelligenceSignal, LinkedNewsItem } from '../types/global'
import type { NewsCategory, NewsItem } from '../types/news'

const normalize = (value: string) => value.toLocaleLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

const companyAliases = new Map(globalCompanies.map((company) => [company.slug, company.slug === 'amazon' ? ['amazon web services', 'aws'] : [company.name, company.shortName, company.ticker].filter(Boolean).map((value) => normalize(value!))]))
const sourceCompanySlugs = new Map<string, string[]>([
  ['aws-ml-blog', ['amazon']],
  ['google-ai', ['google-deepmind']],
  ['nvidia-blog', ['nvidia']],
  ['openai-news', ['openai']],
])
const countryAliases = new Map(countries.map((country) => [country.slug, [country.name, country.iso2].map(normalize)]))
const aiNewsCategories = new Set<NewsCategory>(['AI / COMPUTE', 'DATA CENTERS', 'GRID', 'POWER', 'NUCLEAR / FUEL', 'CAPITAL', 'GOVERNMENT', 'COMMUNITIES'])

export function linkNewsItem(item: NewsItem): LinkedNewsItem {
  const matchedEntities = item.classification?.matchedEntities.join(' ') ?? ''
  const haystack = ` ${normalize(`${item.title} ${item.summary ?? ''} ${item.publisher} ${matchedEntities}`)} `
  const inferredCompanySlugs = Array.from(companyAliases).flatMap(([slug, aliases]) => aliases.some((alias) => alias.length > 2 && haystack.includes(` ${alias} `)) ? [slug] : [])
  const companySlugs = [...new Set([...(item.companySlugs ?? []), ...(sourceCompanySlugs.get(item.sourceId) ?? []), ...inferredCompanySlugs])]
  const existingCountrySlugs = Array.isArray((item as Partial<LinkedNewsItem>).countrySlugs) ? (item as Partial<LinkedNewsItem>).countrySlugs ?? [] : []
  const inferredCountrySlugs = Array.from(countryAliases).flatMap(([slug, aliases]) => aliases.some((alias) => alias.length > 2 && haystack.includes(` ${alias} `)) ? [slug] : [])
  const countrySlugs = [...new Set([...existingCountrySlugs, ...inferredCountrySlugs])]
  return { ...item, companySlugs, countrySlugs }
}

export function selectCompanyNews(items: NewsItem[], companySlug: string): LinkedNewsItem[] {
  const seen = new Set<string>()
  return items
    .map(linkNewsItem)
    .filter((item) => item.kind !== 'ai-synthesis' && item.companySlugs.includes(companySlug))
    .filter((item) => {
      const key = item.url || item.id
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

export function newsToSignals(items: NewsItem[]): IntelligenceSignal[] {
  return items.map(linkNewsItem).map((item) => {
    const categories = item.categories.filter((category): category is NewsCategory => aiNewsCategories.has(category as NewsCategory))
    return ({
    id: `news-${item.id}`,
    title: item.title,
    summary: item.summary ?? 'Open the source for the publisher summary.',
    observedAt: item.publishedAt,
    type: item.categories.includes('CAPITAL') ? 'CAPITAL' : item.categories.includes('GOVERNMENT') ? 'POLICY' : item.categories.includes('DATA CENTERS') ? 'INFRASTRUCTURE' : 'NEWS',
    companySlugs: item.companySlugs,
    countrySlugs: item.countrySlugs,
    categories,
    confidence: item.companySlugs.length || item.countrySlugs.length ? 'high' : 'contextual',
    reviewState: 'discovered',
    evidence: 'sourced',
    sourceUrl: item.url,
    sourceName: item.publisher,
    clusterSize: 1,
    })
  })
}
